/**
 * Grade Room — WebGPU preview engine (P1).
 *
 * Implements THE SAME v1 op math as gradeMath.js / grade_recipe_compiler.py
 * in WGSL: cdl → adjust → curves(1D LUT) → qualifier mix, serial nodes, plus
 * the wipe split. 3D LUT (.cube) nodes and matte view fall back to the CPU
 * engine (PreviewCanvas decides).
 *
 * Chrome/Edge v1 baseline; callers must check isWebGPUAvailable().
 */
import { compileNode } from "./gradeMath";

export const MAX_NODES = 8;
const CURVE_SIZE = 256;

export const isWebGPUAvailable = () => typeof navigator !== "undefined" && !!navigator.gpu;

const WGSL = /* wgsl */ `
struct NodeData {
  slope   : vec4<f32>,  // rgb + cdlActive
  offset  : vec4<f32>,  // rgb + adjActive
  power   : vec4<f32>,  // rgb + curvesActive
  adjust  : vec4<f32>,  // contrast, pivot, saturation, temp
  adjust2 : vec4<f32>,  // tint, qualActive, curveChannelMask, _
  qual    : vec4<f32>,  // hue_min, hue_max, sat_min, sat_max
  qual2   : vec4<f32>,  // lum_min, lum_max, softness, _
};
struct Uniforms {
  meta  : vec4<f32>,    // nodeCount, wipe, _, _
  nodes : array<NodeData, ${MAX_NODES}>,
};
@group(0) @binding(0) var samp : sampler;
@group(0) @binding(1) var tex  : texture_2d<f32>;
@group(0) @binding(2) var<uniform> u : Uniforms;
@group(0) @binding(3) var<storage, read> curves : array<f32>; // [node][ch:4][${CURVE_SIZE}]

fn clamp01v(v : vec3<f32>) -> vec3<f32> { return clamp(v, vec3(0.0), vec3(1.0)); }

fn sample_curve(node : u32, ch : u32, v : f32) -> f32 {
  let f = clamp(v, 0.0, 1.0) * f32(${CURVE_SIZE} - 1);
  let i = u32(floor(f));
  let t = f - floor(f);
  let basei = node * 4u * ${CURVE_SIZE}u + ch * ${CURVE_SIZE}u;
  let i1 = min(i + 1u, ${CURVE_SIZE}u - 1u);
  return curves[basei + i] * (1.0 - t) + curves[basei + i1] * t;
}

fn rgb2hsl(c : vec3<f32>) -> vec3<f32> {
  let mx = max(c.r, max(c.g, c.b));
  let mn = min(c.r, min(c.g, c.b));
  let l = (mx + mn) * 0.5;
  if (mx == mn) { return vec3(0.0, 0.0, l); }
  let dd = mx - mn;
  var s : f32;
  if (l > 0.5) { s = dd / (2.0 - mx - mn); } else { s = dd / (mx + mn); }
  var h : f32;
  if (mx == c.r) {
    h = (c.g - c.b) / dd;
    if (c.g < c.b) { h = h + 6.0; }
  } else if (mx == c.g) { h = (c.b - c.r) / dd + 2.0; }
  else { h = (c.r - c.g) / dd + 4.0; }
  return vec3(h * 60.0, s, l);
}

fn band_mask(v : f32, lo : f32, hi : f32, feather : f32) -> f32 {
  if (v >= lo && v <= hi) { return 1.0; }
  if (v < lo - feather || v > hi + feather) { return 0.0; }
  if (v < lo) { return (v - (lo - feather)) / feather; }
  return ((hi + feather) - v) / feather;
}

fn hue_mask(h : f32, lo : f32, hi : f32, feather : f32) -> f32 {
  if (lo <= hi) { return band_mask(h, lo, hi, feather); }
  return max(band_mask(h, lo, 360.0 + hi, feather),
             band_mask(h + 360.0, lo, 360.0 + hi, feather));
}

@vertex
fn vs(@builtin(vertex_index) vi : u32) -> @builtin(position) vec4<f32> {
  var pos = array<vec2<f32>, 3>(vec2(-1.0, -3.0), vec2(3.0, 1.0), vec2(-1.0, 1.0));
  return vec4(pos[vi], 0.0, 1.0);
}

@fragment
fn fs(@builtin(position) fragPos : vec4<f32>) -> @location(0) vec4<f32> {
  let dims = vec2<f32>(textureDimensions(tex));
  let uv = fragPos.xy / dims;
  let src = textureSampleLevel(tex, samp, uv, 0.0);
  var rgb = src.rgb;

  // wipe: right of split shows the ORIGINAL
  if (uv.x > u.meta.y) { return vec4(src.rgb, 1.0); }

  let n = u32(u.meta.x);
  for (var i = 0u; i < n; i = i + 1u) {
    let nd = u.nodes[i];
    let input = rgb;
    if (nd.slope.w > 0.5) {                        // cdl
      rgb = pow(clamp01v(rgb * nd.slope.rgb + nd.offset.rgb), nd.power.rgb);
    }
    if (nd.offset.w > 0.5) {                       // adjust
      let contrast = nd.adjust.x; let pivot = nd.adjust.y;
      let sat = nd.adjust.z; let temp = nd.adjust.w; let tint = nd.adjust2.x;
      rgb = (rgb - vec3(pivot)) * contrast + vec3(pivot);
      let luma = dot(rgb, vec3(0.2126, 0.7152, 0.0722));
      rgb = vec3(luma) + (rgb - vec3(luma)) * sat
          + vec3(temp * 0.1, tint * 0.1, -temp * 0.1);
      rgb = clamp01v(rgb);
    }
    if (nd.power.w > 0.5) {                        // curves
      let m = u32(nd.adjust2.z);                   // channel bitmask M=1,R=2,G=4,B=8
      if ((m & 1u) != 0u) {
        rgb = vec3(sample_curve(i, 0u, rgb.r), sample_curve(i, 0u, rgb.g), sample_curve(i, 0u, rgb.b));
      }
      if ((m & 2u) != 0u) { rgb.r = sample_curve(i, 1u, rgb.r); }
      if ((m & 4u) != 0u) { rgb.g = sample_curve(i, 2u, rgb.g); }
      if ((m & 8u) != 0u) { rgb.b = sample_curve(i, 3u, rgb.b); }
    }
    if (nd.adjust2.y > 0.5) {                      // qualifier mix
      let hsl = rgb2hsl(input);
      let soft = nd.qual2.z;
      let fh = max(0.0001, soft * 30.0);
      let fs = max(0.0001, soft * max(nd.qual.w - nd.qual.z, 1.0) * 0.5);
      let fl = max(0.0001, soft * max(nd.qual2.y - nd.qual2.x, 1.0) * 0.5);
      let mask = hue_mask(hsl.x, nd.qual.x, nd.qual.y, fh)
        * band_mask(hsl.y, nd.qual.z, nd.qual.w, fs)
        * band_mask(hsl.z, nd.qual2.x, nd.qual2.y, fl);
      rgb = input + (rgb - input) * mask;
    }
  }
  return vec4(rgb, 1.0);
}
`;

export class WebGPUGradeEngine {
  constructor() {
    this.device = null;
    this.ready = false;
  }

  async init(canvas) {
    const adapter = await navigator.gpu.requestAdapter();
    if (!adapter) throw new Error("no WebGPU adapter");
    this.device = await adapter.requestDevice();
    this.canvas = canvas;
    this.ctx = canvas.getContext("webgpu");
    this.format = navigator.gpu.getPreferredCanvasFormat();
    this.ctx.configure({ device: this.device, format: this.format, alphaMode: "opaque" });

    const module = this.device.createShaderModule({ code: WGSL });
    this.pipeline = this.device.createRenderPipeline({
      layout: "auto",
      vertex: { module, entryPoint: "vs" },
      fragment: { module, entryPoint: "fs", targets: [{ format: this.format }] },
      primitive: { topology: "triangle-list" },
    });
    this.sampler = this.device.createSampler({ magFilter: "linear", minFilter: "linear" });
    // meta(16B) + 8 nodes × 7 vec4 (112B) = 912B
    this.uniformBuf = this.device.createBuffer({
      size: 16 + MAX_NODES * 7 * 16,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.curveBuf = this.device.createBuffer({
      size: MAX_NODES * 4 * CURVE_SIZE * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    this.ready = true;
  }

  setSource(imageBitmap) {
    const { width, height } = imageBitmap;
    this.canvas.width = width;
    this.canvas.height = height;
    this.texture = this.device.createTexture({
      size: [width, height],
      format: "rgba8unorm",
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST |
        GPUTextureUsage.RENDER_ATTACHMENT,
    });
    this.device.queue.copyExternalImageToTexture(
      { source: imageBitmap }, { texture: this.texture }, [width, height]);
    this.bindGroup = this.device.createBindGroup({
      layout: this.pipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: this.sampler },
        { binding: 1, resource: this.texture.createView() },
        { binding: 2, resource: { buffer: this.uniformBuf } },
        { binding: 3, resource: { buffer: this.curveBuf } },
      ],
    });
  }

  render(nodes, wipe) {
    if (!this.ready || !this.bindGroup) return;
    const enabled = (nodes || []).filter((n) => n.enabled !== false).slice(0, MAX_NODES);
    const compiled = enabled.map((n) => compileNode(n, {}));

    const uni = new Float32Array(4 + MAX_NODES * 7 * 4);
    uni[0] = compiled.length;
    uni[1] = wipe;
    const curveData = new Float32Array(MAX_NODES * 4 * CURVE_SIZE);
    // identity ramp default so disabled channels are harmless
    compiled.forEach((c, i) => {
      const o = 4 + i * 28;
      uni.set([...c.slope, c.cdlActive ? 1 : 0], o);
      uni.set([...c.offset, c.adjActive ? 1 : 0], o + 4);
      let chMask = 0;
      const chans = ["master", "r", "g", "b"];
      chans.forEach((ch, k) => {
        const lut = c.curveLuts[ch];
        if (lut) {
          chMask |= 1 << k;
          curveData.set(lut, (i * 4 + k) * CURVE_SIZE);
        }
      });
      uni.set([...c.power, c.curvesActive ? 1 : 0], o + 8);
      uni.set([c.adj.contrast, c.adj.pivot, c.adj.saturation, c.adj.temp], o + 12);
      uni.set([c.adj.tint, c.qual ? 1 : 0, chMask, 0], o + 16);
      const q = c.qual || {};
      uni.set([q.hue_min || 0, q.hue_max || 360, q.sat_min || 0, q.sat_max ?? 1], o + 20);
      uni.set([q.lum_min || 0, q.lum_max ?? 1, q.softness ?? 0.25, 0], o + 24);
    });
    this.device.queue.writeBuffer(this.uniformBuf, 0, uni);
    this.device.queue.writeBuffer(this.curveBuf, 0, curveData);

    const encoder = this.device.createCommandEncoder();
    const pass = encoder.beginRenderPass({
      colorAttachments: [{
        view: this.ctx.getCurrentTexture().createView(),
        loadOp: "clear", storeOp: "store", clearValue: { r: 0, g: 0, b: 0, a: 1 },
      }],
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    pass.draw(3);
    pass.end();
    this.device.queue.submit([encoder.finish()]);
  }

  destroy() {
    try { this.texture?.destroy(); this.uniformBuf?.destroy(); this.curveBuf?.destroy(); } catch (e) { /* noop */ }
    this.ready = false;
  }
}
